import { useMutation, useQueryClient } from 'react-query';
import { useDispatch } from 'react-redux';

import { toastrError, toastrSuccess } from '@portals/redux/actions/toastr';
import { SupportedCommandType } from '@portals/types';

import { useApiQuery } from '../../hooks';
import { ServerError } from '../../types';
import { fetchApiRequest, useRequestOptions } from '../../utils';
import { deviceModelsQueryKeys } from './device-models.constants';

const getSupportedCommandsApiUrl = (modelId: string) =>
  `ui/partner/device_models/${modelId}/supported_commands`;

export const useSupportedCommands = (modelId: string) => {
  return useApiQuery<SupportedCommandType[]>(
    getSupportedCommandsApiUrl(modelId),
    deviceModelsQueryKeys.supportedCommands.all(modelId),
    {
      staleTime: 0,
      enabled: Boolean(modelId),
    }
  );
};

export const useDeleteSupportedCommand = (modelId: string) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const { url, options } = useRequestOptions({
    url: `${getSupportedCommandsApiUrl(modelId)}`,
    method: 'DELETE',
  });

  return useMutation<void, ServerError, string>({
    mutationFn: (commandId) => fetchApiRequest(`${url}/${commandId}`, options),
    onSuccess: () => {
      dispatch(toastrSuccess('Command successfully removed'));

      queryClient.invalidateQueries(deviceModelsQueryKeys.detail(modelId));
    },
    onError: ({ error }) => {
      dispatch(toastrError('Command removal failed', error));
    },
  });
};

export const useAddSupportedCommand = (modelId: string) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const { url, options } = useRequestOptions({
    url: getSupportedCommandsApiUrl(modelId),
    method: 'POST',
  });

  return useMutation<void, ServerError, Partial<SupportedCommandType>>({
    mutationFn: (supportedCommand) =>
      fetchApiRequest(url, {
        ...options,
        body: JSON.stringify(supportedCommand),
      }),
    onSuccess: async () => {
      dispatch(toastrSuccess('Custom command added'));

      queryClient.invalidateQueries(deviceModelsQueryKeys.detail(modelId));
    },
    onError: ({ error }) => {
      return dispatch(toastrError(error));
    },
  });
};

export const useUpdateSupportedCommand = (modelId: string) => {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { url, options } = useRequestOptions({
    url: getSupportedCommandsApiUrl(modelId),
    method: 'PUT',
  });

  return useMutation<void, ServerError, Partial<SupportedCommandType>>({
    mutationFn: (supportedCommand) =>
      fetchApiRequest(`${url}/${supportedCommand.id}`, {
        ...options,
        body: JSON.stringify(supportedCommand),
      }),
    onSuccess: () => {
      dispatch(toastrSuccess('Custom command updated'));

      queryClient.invalidateQueries(deviceModelsQueryKeys.detail(modelId));
    },
    onError: ({ error }) => {
      dispatch(toastrError(error));
    },
  });
};

export interface UseMoveSupportedCommandParams {
  commandId: string;
  isUp: boolean;
}

export const useMoveSupportedCommand = (modelId: string) => {
  const queryClient = useQueryClient();
  const { url, options } = useRequestOptions({
    url: getSupportedCommandsApiUrl(modelId),
    method: 'PUT',
  });

  return useMutation<void, ServerError, UseMoveSupportedCommandParams>({
    mutationFn: ({ commandId, isUp }) =>
      fetchApiRequest(
        `${url}/${commandId}/move_${isUp ? 'up' : 'down'}`,
        options
      ),
    onSuccess: () => {
      queryClient.invalidateQueries(deviceModelsQueryKeys.detail(modelId));
    },
  });
};
